import L from 'leaflet';

/**
 * Ícones e cores de pins compartilhados entre mapas (Roteirizacao.vue, CRUDCandidatos.vue).
 * O HTML do divIcon é injetado fora do Vue: estilos de componente (scoped) não se aplicam,
 * então o pin circular usa inline styles; as classes `roteirizacao-map-*` vêm do CSS de Roteirizacao.vue.
 */

/**
 * `pessoas_enderecos_validados.valido`: 1 Atende, 2 sem vaga, 3 criação de ponto, 4 fora unidade,
 * 5 não atende por tempo na linha, else não atende.
 * Cor do mapa: melhor resultado entre turnos (atende > criação de parada > sem vaga > não atende > fora de unidade).
 */
export const MAP_VALIDATION_COLOR = {
    1: '#2E7D32',
    2: '#F57C00',
    3: '#1565C0',
    4: '#C62828',
    other: '#B71C1C',
    none: '#9E9E9E',
};

/** Maior = melhor, para escolher o `valido` a exibir no marcador entre vários turnos. */
export function mapMarkerPriorityRank(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return -1;
    if (n === 1) return 5;
    if (n === 3) return 4;
    if (n === 2) return 3;
    if (n === 4) return 1;
    return 2;
}

export function mapColorForValidoCode(v) {
    const n = Number(v);
    if (n === 1) return MAP_VALIDATION_COLOR[1];
    if (n === 2) return MAP_VALIDATION_COLOR[2];
    if (n === 3) return MAP_VALIDATION_COLOR[3];
    if (n === 4) return MAP_VALIDATION_COLOR[4];
    if (Number.isFinite(n)) return MAP_VALIDATION_COLOR.other;
    return MAP_VALIDATION_COLOR.none;
}

/**
 * Pin circular de validação de endereço (visual de CRUDCandidatos).
 * `tooltipAnchor` é opcional: sem ele mantém o padrão do Leaflet.
 */
export function buildCandidatosStyleMapIcon(color, { tooltipAnchor } = {}) {
    const safeColor = String(color || MAP_VALIDATION_COLOR.none).replace(/[<>"';]/g, '');
    const dot =
        '<div style="width:14px;height:14px;border-radius:50%;background-color:' +
        safeColor +
        ';border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.35);margin:3px;"></div>';
    const options = {
        className: 'candidatos-map-divicon',
        html: dot,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
    };
    if (tooltipAnchor) options.tooltipAnchor = tooltipAnchor;
    return L.divIcon(options);
}

function escapeIconText(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const ROUTE_STOP_BALLOON_PATH =
    'M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z';

/**
 * Pin balão (estilo Google Maps) na cor da rota.
 * Com `info.order`, o miolo do balão mostra a ordem da parada e o hover expande um cartão
 * (textos já traduzidos): `title`, `rows` e, quando houver, `addresses` num `<details>`
 * colapsável rotulado por `addressesLabel`. Sem `info`, mantém o balão com o círculo branco
 * (paradas da Alocação).
 */
export function buildRouteStopMapIcon(color, info = null) {
    const safeColor = String(color || '#9E9E9E').replace(/[<>"']/g, '');
    const order = info && info.order != null ? String(info.order) : '';
    const center = order
        ? '<circle cx="14" cy="13" r="9" fill="#fff"/>' +
          `<text x="14" y="13" text-anchor="middle" dominant-baseline="central" font-size="${order.length > 2 ? 9 : 11}" font-weight="700" fill="${safeColor}">${escapeIconText(order)}</text>`
        : '<circle cx="14" cy="13" r="5" fill="#fff"/>';
    const balloon =
        '<div class="roteirizacao-map-balloon roteirizacao-map-stop__pin" aria-hidden="true">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40" focusable="false">' +
        `<path d="${ROUTE_STOP_BALLOON_PATH}" fill="${safeColor}" stroke="#fff" stroke-width="1.5"/>` +
        center +
        '</svg></div>';
    const rows = info && Array.isArray(info.rows) ? info.rows.filter(Boolean) : [];
    const addresses = info && Array.isArray(info.addresses) ? info.addresses.filter(Boolean) : [];
    const details = addresses.length
        ? '<details class="roteirizacao-map-stop__details">' +
          `<summary>${escapeIconText(info.addressesLabel)}</summary>` +
          '<ul class="roteirizacao-map-stop__list">' +
          addresses.map((a) => `<li>${escapeIconText(a)}</li>`).join('') +
          '</ul></details>'
        : '';
    const html = order
        ? '<div class="roteirizacao-map-stop">' +
          balloon +
          `<div class="roteirizacao-map-stop__card" style="background-color:${safeColor}">` +
          `<div class="roteirizacao-map-stop__line roteirizacao-map-stop__line--title">${escapeIconText(info.title || order)}</div>` +
          rows.map((l) => `<div class="roteirizacao-map-stop__line">${escapeIconText(l)}</div>`).join('') +
          details +
          '</div></div>'
        : balloon;
    return L.divIcon({
        className: 'roteirizacao-map-divicon',
        html,
        iconSize: [28, 40],
        iconAnchor: [14, 40],
        tooltipAnchor: [0, -36],
    });
}

/** Pin rotulado (D, G, !) — `variant` seleciona a cor via `roteirizacao-map-pin--{variant}`. */
function buildLabeledPinIcon(variant, label) {
    return L.divIcon({
        className: 'roteirizacao-map-divicon',
        html: `<div class="roteirizacao-map-pin roteirizacao-map-pin--${variant}" aria-hidden="true"><span class="roteirizacao-map-pin-label">${label}</span></div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 34],
        tooltipAnchor: [0, -30],
    });
}

export const DESTINATION_MAP_ICON = buildLabeledPinIcon('depot', 'D');
export const BASE_MAP_ICON = buildLabeledPinIcon('base', 'G');
export const INVALID_STOP_MAP_ICON = buildLabeledPinIcon('invalid', '!');
